import ContentSaveIcon from '@mui/icons-material/Save';
import { Button as MuiButton, Stack } from '@mui/material';
import { FormEvent, useEffect, useMemo } from 'react';
import {
    RecordContextProvider,
    TextInput,
    Toolbar,
    required,
    useCreate,
    useDelete,
    useNotify,
    useUpdate,
} from 'react-admin';
import { FormProvider, useForm } from 'react-hook-form';
import type { HighlightedResourceSummary, ResourceType } from '@wwf/sdk';
import { isUrl } from '@app/misc/isUrl';
import { UrlInput } from '@app/misc/UrlInput';
import { showModeReadOnlySx } from '@app/root/theme';

type DrawerMode = 'create' | 'edit' | 'show';

type HighlightedResourceFormValues = {
    name: string;
    url: string;
};

const DEFAULT_TYPE: ResourceType = 'DOCUMENT';

const defaultValues: HighlightedResourceFormValues = {
    name: '',
    url: '',
};

export function HighlightedResourceDrawerForm({
    mode,
    record,
    loading,
    onClose,
    onSuccess,
}: {
    mode: DrawerMode;
    record?: HighlightedResourceSummary;
    loading: boolean;
    onClose(): void;
    onSuccess(): void;
}) {
    const notify = useNotify();
    const [create] = useCreate<HighlightedResourceSummary>();
    const [update] = useUpdate<HighlightedResourceSummary>();
    const [deleteOne] = useDelete<HighlightedResourceSummary>();

    const form = useForm<HighlightedResourceFormValues>({
        defaultValues,
    });

    const formRecord = useMemo(
        () =>
            record && (mode === 'edit' || mode === 'show')
                ? {
                      name: record.name ?? '',
                      url: record.url ?? '',
                  }
                : undefined,
        [record, mode]
    );

    useEffect(() => {
        if (formRecord) {
            form.reset(formRecord);
        } else {
            form.reset(defaultValues);
        }
    }, [formRecord, form]);

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (mode === 'show') return;
        form.handleSubmit(async (data: HighlightedResourceFormValues) => {
            try {
                if (mode === 'create') {
                    const payload = {
                        name: (data.name ?? '').trim(),
                        url: (data.url ?? '').trim(),
                        type: DEFAULT_TYPE,
                    };

                    await create(
                        'highlightedResource',
                        { data: payload as Record<string, unknown> },
                        {
                            onSuccess: () => {
                                onSuccess();
                                onClose();
                            },
                        }
                    );
                } else {
                    if (!record?.id) {
                        notify('Record not found', { type: 'error' });
                        return;
                    }

                    await update(
                        'highlightedResource',
                        {
                            id: record.id,
                            data: {
                                name: (data.name ?? '').trim(),
                                url: (data.url ?? '').trim() as any,
                            },
                            previousData: record,
                        },
                        {
                            onSuccess: () => {
                                onSuccess();
                                onClose();
                            },
                        }
                    );
                }
            } catch (e) {
                notify(String(e), { type: 'error' });
            }
        })(event);
    };

    const handleDelete = () => {
        if (!record?.id) return;
        deleteOne(
            'highlightedResource',
            { id: record.id, previousData: record },
            {
                onSuccess: () => {
                    onSuccess();
                    onClose();
                },
            }
        );
    };

    const saving = form.formState.isSubmitting;
    const canSave = !loading && !saving;

    return (
        <FormProvider {...form}>
            <RecordContextProvider value={formRecord ?? undefined}>
                <form onSubmit={handleSubmit}>
                    <Stack
                        gap={2}
                        sx={mode === 'show' ? showModeReadOnlySx : undefined}
                    >
                        <TextInput
                            source="name"
                            label="Name"
                            autoFocus
                            validate={required()}
                            readOnly={mode === 'show' || loading}
                        />
                        <UrlInput
                            source="url"
                            label="URL"
                            validate={[required(), isUrl]}
                            readOnly={mode === 'show' || loading}
                        />
                    </Stack>

                    {mode !== 'show' && (
                        <Toolbar
                            sx={{
                                mt: 2,
                                px: 0,
                                justifyContent: 'space-between',
                            }}
                        >
                            <Stack direction="row" gap={1}>
                                <MuiButton
                                    variant="contained"
                                    type="submit"
                                    disabled={!canSave}
                                    startIcon={<ContentSaveIcon />}
                                >
                                    Save
                                </MuiButton>
                                <MuiButton
                                    variant="text"
                                    onClick={onClose}
                                    disabled={saving}
                                >
                                    Cancel
                                </MuiButton>
                            </Stack>
                            {mode === 'edit' && record?.id && (
                                <MuiButton
                                    variant="text"
                                    color="error"
                                    disabled={saving}
                                    onClick={() => {
                                        if (
                                            window.confirm(
                                                'Are you sure you want to delete this highlighted resource?'
                                            )
                                        ) {
                                            handleDelete();
                                        }
                                    }}
                                >
                                    Delete
                                </MuiButton>
                            )}
                        </Toolbar>
                    )}
                </form>
            </RecordContextProvider>
        </FormProvider>
    );
}
